import { I18NEXT_LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from '@/constants/languages'
import { normalizeToSupportedLanguageCode } from '@/utils/languageCodeUtils'
import { useUser } from '@clerk/clerk-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const SUPPORTED_LANGUAGE_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code))

/**
 * Synchronizes the i18next language with the user's persisted preference.
 * Checks localStorage first, then falls back to Clerk user metadata.
 */
export function useLanguageSync(): void {
  const { user } = useUser()
  const { i18n } = useTranslation()

  useEffect(() => {
    const clerkPreferred = user?.unsafeMetadata?.preferredLanguage as string | undefined
    let storedPreferred: string | null = null

    if (typeof window !== 'undefined') {
      try {
        storedPreferred = window.localStorage.getItem(I18NEXT_LANGUAGE_STORAGE_KEY)
      } catch {
        storedPreferred = null
      }
    }

    const normalizedStored = normalizeToSupportedLanguageCode(storedPreferred)
    const normalizedClerk = normalizeToSupportedLanguageCode(clerkPreferred)

    const nextLang =
      (normalizedStored && SUPPORTED_LANGUAGE_CODES.has(normalizedStored) ? normalizedStored : undefined) ??
      (normalizedClerk && SUPPORTED_LANGUAGE_CODES.has(normalizedClerk) ? normalizedClerk : undefined)

    if (nextLang && i18n.language !== nextLang) {
      i18n.changeLanguage(nextLang).catch(() => {
        // Ignore; fallbackLng will handle display
      })
    }
  }, [user?.unsafeMetadata?.preferredLanguage, i18n])
}
